import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98]",
  {
    variants: {
      variant: {
        // Primary with gradient and glow
        default:
          "bg-primary text-primary-foreground border border-primary-border shadow-sm hover:shadow-glow-primary hover:brightness-110 active:brightness-95",

        // Premium gold gradient button
        premium:
          "bg-gradient-to-br from-amber-400 via-amber-500 to-orange-500 text-slate-900 font-semibold shadow-md hover:shadow-glow-gold hover:scale-[1.02] hover:brightness-105 border border-amber-600/30",

        // Glass morphism button
        glass:
          "backdrop-blur-md bg-white/10 dark:bg-white/5 border border-white/20 text-foreground hover:bg-white/20 dark:hover:bg-white/10 shadow-lg",

        // Destructive with glow
        destructive:
          "bg-destructive text-destructive-foreground border border-destructive-border shadow-sm hover:shadow-glow-error hover:brightness-110",

        // Outline with premium hover
        outline:
          "border-2 border-primary/30 bg-transparent shadow-xs hover:bg-primary/5 hover:border-primary text-primary hover:shadow-sm",

        // Secondary
        secondary:
          "bg-secondary text-secondary-foreground border border-secondary-border shadow-xs hover:bg-secondary/80 hover:shadow-sm",

        // Ghost with subtle hover
        ghost:
          "border border-transparent hover:bg-accent hover:text-accent-foreground",

        // Link style
        link:
          "text-primary underline-offset-4 hover:underline",

        // Success variant
        success:
          "bg-success text-success-foreground border border-success/30 shadow-sm hover:shadow-glow-success hover:brightness-110",

        // Warning variant
        warning:
          "bg-warning text-warning-foreground border border-warning/30 shadow-sm hover:shadow-glow-warning hover:brightness-110",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-12 rounded-xl px-8 text-base",
        xl: "h-14 rounded-xl px-10 text-lg font-semibold",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
        "icon-lg": "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = "Button"

export { Button, buttonVariants }
